/** Third tab of `/gis-queue` (UX remediation Stage 3, C3-rest / decision D-8):
 *  the GIS-cleanup worklists that previously had no home —
 *   - silent-meter transformers: measured-basis capacity rows where
 *     `meters_missing > 0` (meters linked in the platform that sent no AMI
 *     readings over the window — usually a wrong meter-transformer link in
 *     GIS or a dead meter);
 *   - unrooted secondary components: review-queue rows whose `why_flagged`
 *     is 'unrooted' (a low-voltage run serving meters but connected to no
 *     transformer);
 *   - fed-by mismatches (spec 2026-09-02 Phase 4.4): findings where the map
 *     data's fed-by pointer names a device the protection run did not compute
 *     on the path to the source.
 *  Read-only surfaces + CSV export via the shared utils/csv.ts::exportCsv.
 *  ACTIONS deliberately stay on their owning pages (/engineering capacity,
 *  /secondary-review) or in the field / a GIS packet — this tab is triage
 *  orientation, not a second write path. Derivation logic lives in
 *  ./worklists.ts (pure, unit-tested). */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { appPath } from '../../appConfig.ts'
import { getTransformerLoading } from '../../api/engineering.ts'
import { getFedByFindings } from '../../api/fedBy.ts'
import {
  getPoleDisagreements,
  getPolesWithoutTransformer,
  getSecondaryDisagreements,
} from '../../api/linkEvidence.ts'
import { getReviewQueue } from '../../api/secondary.ts'
import { exportCsv } from '../../utils/csv.ts'
import {
  csvFilename,
  fedByCsvRows,
  poleDisagreementCsvRows,
  polesWithoutTransformerCsvRows,
  secondaryDisagreementCsvRows,
  silentMeterCsvRows,
  silentMeterRows,
  unrootedCsvRows,
  unrootedRows,
} from './worklists.ts'

/** Backend clamp on GET /secondary/review-queue (api/v1/secondary.py: le=200).
 *  With Task 10.1's `why_flagged` filter these are 200 UNROOTED rows, and the
 *  response `total` counts only unrooted components — so the page range below
 *  is honest instead of "the first 200 of the whole ambiguous bucket". */
const REVIEW_QUEUE_FETCH_LIMIT = 200

/** Backend clamp on GET /engineering/fed-by-findings (api/v1/engineering.py:
 *  le=500). 200 keeps the page comparable to the review queue's. */
const FED_BY_FETCH_LIMIT = 200

/** Backend clamp on the three GET /engineering/link-evidence/* worklists
 *  (api/v1/engineering.py: le=500 each). 200 keeps every worklist page on
 *  this tab the same size. */
const EVIDENCE_FETCH_LIMIT = 200

/** Plain-language gloss for a fed-by finding's `reason` (unresolved rows only:
 *  no_value | unmatched | ambiguous | self_reference). An unmapped value falls
 *  through as itself rather than being swallowed. */
const UNRESOLVED_REASON: Record<string, string> = {
  no_value: 'the record is a marker, not a device',
  unmatched: 'no device by that name',
  ambiguous: 'more than one device by that name',
  self_reference: 'the record names itself',
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString()
}

export function WorklistsPanel() {
  const silentQuery = useQuery({
    queryKey: ['gis-queue', 'worklist', 'silent-meters'],
    queryFn: () => getTransformerLoading({ load_source: 'measured' }),
  })
  // Skip-based paging over the SERVER-FILTERED unrooted set (Task 10.1). The
  // review query orders by (run_id, component_id) with no reason precedence, so
  // filtering client-side over one clamped page would silently truncate this
  // worklist; `skip` sits in the query key so a page turn refetches.
  const [skip, setSkip] = useState(0)
  const unrootedQuery = useQuery({
    queryKey: ['gis-queue', 'worklist', 'unrooted-secondary', skip],
    queryFn: () =>
      getReviewQueue({
        why_flagged: 'unrooted',
        skip,
        limit: REVIEW_QUEUE_FETCH_LIMIT,
      }),
  })

  // Server-paged over the MISMATCH set (off_chain + other_feeder): the endpoint
  // applies that default, so `total` is the honest mismatch count, not the whole
  // fed-by population.
  const [fedSkip, setFedSkip] = useState(0)
  const fedQuery = useQuery({
    queryKey: ['gis-queue', 'worklist', 'fed-by', fedSkip],
    queryFn: () => getFedByFindings({ skip: fedSkip, limit: FED_BY_FETCH_LIMIT }),
  })
  const fedRows = fedQuery.data?.items ?? []
  const fedTotal = fedQuery.data?.total ?? 0
  const fedPaged = fedTotal > FED_BY_FETCH_LIMIT
  // `run_id` is null when no protection run has completed yet, distinct from
  // a completed run that predates this check (finds nothing => empty items
  // with a real run_id) -- the two empty states below tell them apart.
  const fedRunId = fedQuery.data?.run_id ?? null

  // Secondary disagreements: published-run attachments that disagree with a
  // meter's linked transformer. `secFar` is the "> N m" checkbox; the metre
  // value it filters on is never a literal here -- `secThreshold` only ever
  // holds what the LAST LOADED page reported as far_threshold_m
  // (METER_FAR_FROM_LINKED_M, echoed by every evidence page), so the checkbox
  // stays disabled until a page has actually loaded. Adjusted during render
  // (not a useEffect -- react-hooks/set-state-in-effect forbids mirroring a
  // query result into state from an effect body); the equality guard means
  // this only fires the one render where a new page actually landed.
  const [secSkip, setSecSkip] = useState(0)
  const [secFar, setSecFar] = useState(false)
  const [secThreshold, setSecThreshold] = useState<number | null>(null)
  const secQuery = useQuery({
    queryKey: ['gis-queue', 'worklist', 'secondary-disagreements', secSkip, secFar],
    queryFn: () =>
      getSecondaryDisagreements({
        skip: secSkip,
        limit: EVIDENCE_FETCH_LIMIT,
        min_distance_m: secFar ? (secThreshold ?? undefined) : undefined,
      }),
  })
  if (secQuery.data && secQuery.data.far_threshold_m !== secThreshold) {
    setSecThreshold(secQuery.data.far_threshold_m)
  }
  const secRows = secQuery.data?.items ?? []
  const secTotal = secQuery.data?.total ?? 0
  const secPaged = secTotal > EVIDENCE_FETCH_LIMIT

  // Pole disagreements: same shape as secondary disagreements, keyed off the
  // pole (PT_XFMR_LOC) instead of the secondary network.
  const [poleSkip, setPoleSkip] = useState(0)
  const [poleFar, setPoleFar] = useState(false)
  const [poleThreshold, setPoleThreshold] = useState<number | null>(null)
  const poleQuery = useQuery({
    queryKey: ['gis-queue', 'worklist', 'pole-disagreements', poleSkip, poleFar],
    queryFn: () =>
      getPoleDisagreements({
        skip: poleSkip,
        limit: EVIDENCE_FETCH_LIMIT,
        min_distance_m: poleFar ? (poleThreshold ?? undefined) : undefined,
      }),
  })
  if (poleQuery.data && poleQuery.data.far_threshold_m !== poleThreshold) {
    setPoleThreshold(poleQuery.data.far_threshold_m)
  }
  const poleRows = poleQuery.data?.items ?? []
  const poleTotal = poleQuery.data?.total ?? 0
  const polePaged = poleTotal > EVIDENCE_FETCH_LIMIT

  // Poles with no platform transformer: a GIS item (no active transformer
  // matches the pole reference at all), not a meter-link disagreement -- no
  // "> N m" filter applies since there is no linked transformer to measure
  // a gap against.
  const [noXfmrSkip, setNoXfmrSkip] = useState(0)
  const noXfmrQuery = useQuery({
    queryKey: ['gis-queue', 'worklist', 'poles-without-transformer', noXfmrSkip],
    queryFn: () => getPolesWithoutTransformer({ skip: noXfmrSkip, limit: EVIDENCE_FETCH_LIMIT }),
  })
  const noXfmrRows = noXfmrQuery.data?.items ?? []
  const noXfmrTotal = noXfmrQuery.data?.total ?? 0
  const noXfmrPaged = noXfmrTotal > EVIDENCE_FETCH_LIMIT

  const silentRows = silentQuery.data ? silentMeterRows(silentQuery.data.transformers) : []
  // `unrootedRows` stays as the client-side guard + the most-meters-first sort;
  // the server filter is what makes the count and the paging honest.
  const unrooted = unrootedQuery.data ? unrootedRows(unrootedQuery.data.items) : []
  /** The FILTERED total — unrooted components only, not the whole review bucket. */
  const reviewTotal = unrootedQuery.data?.total ?? 0
  const paged = reviewTotal > REVIEW_QUEUE_FETCH_LIMIT
  const amiAsOf = fmtDate(silentQuery.data?.as_of ?? null)

  return (
    <div className="space-y-4">
      <section className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-gray-700">
            Transformers with silent meters{' '}
            <span className="text-gray-400 ml-1">({silentRows.length})</span>
          </h3>
          <button
            type="button"
            onClick={() =>
              exportCsv(csvFilename('silent-meter-transformers'), silentMeterCsvRows(silentRows))
            }
            disabled={silentRows.length === 0}
            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export silent-meter worklist (CSV)
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Meters linked to these transformers sent no readings over the AMI window
          {amiAsOf ? ` (as of ${amiAsOf})` : ''} — usually a wrong meter link in the map data or a
          dead meter. Fix the link or flag the meter for a field check.
        </p>
        {silentQuery.isLoading && (
          <div className="text-sm text-gray-500 py-6 text-center">Loading...</div>
        )}
        {silentQuery.isError && (
          <div className="text-sm text-red-600 py-6 text-center">
            Couldn&apos;t load the silent-meter worklist:{' '}
            {silentQuery.error instanceof Error ? silentQuery.error.message : 'unknown error'}
          </div>
        )}
        {!silentQuery.isLoading && !silentQuery.isError && silentRows.length === 0 && (
          <div className="text-sm text-gray-500 py-6 text-center">
            Every measured transformer had all its meters reporting — nothing to clean up.
          </div>
        )}
        {silentRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="p-2">Transformer</th>
                  <th className="p-2">Feeder</th>
                  <th className="p-2 text-right">Meters</th>
                  <th className="p-2 text-right">Silent</th>
                  <th className="p-2 text-right">kVA</th>
                  <th className="p-2 text-right">Effective load</th>
                </tr>
              </thead>
              <tbody>
                {silentRows.map(t => (
                  <tr key={t.id} className="border-b border-gray-100">
                    <td className="p-2 text-xs text-gray-700">{t.name ?? t.gis_id ?? `#${t.id}`}</td>
                    <td className="p-2 text-xs text-gray-600">{t.feeder_name ?? '—'}</td>
                    <td className="p-2 text-xs text-gray-600 text-right">{t.meter_count}</td>
                    <td className="p-2 text-xs text-right font-medium text-amber-700">
                      {t.meters_missing}
                    </td>
                    <td className="p-2 text-xs text-gray-600 text-right">{t.kva_rating ?? '—'}</td>
                    <td className="p-2 text-xs text-gray-600 text-right">
                      {t.effective_loading_pct != null
                        ? `${t.effective_loading_pct.toFixed(1)}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-gray-700">
            Secondary runs with no transformer{' '}
            <span className="text-gray-400 ml-1">({reviewTotal})</span>
          </h3>
          <div className="flex items-center gap-2">
            <Link to={appPath('/secondary-review')} className="text-xs text-blue-600 hover:underline">
              Open secondary review
            </Link>
            <button
              type="button"
              onClick={() =>
                exportCsv(csvFilename('unrooted-secondary-components'), unrootedCsvRows(unrooted))
              }
              disabled={unrooted.length === 0}
              className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Export unrooted worklist (CSV)
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          These low-voltage runs serve meters but connect to no transformer in the model — usually
          a missing or mis-drawn secondary span in the map data. Decide each one in Secondary
          review; export the list for the GIS editor.
        </p>
        {paged && (
          <>
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setSkip(s => Math.max(0, s - REVIEW_QUEUE_FETCH_LIMIT))}
                disabled={skip === 0}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Showing {skip + 1}–{skip + unrooted.length} of {reviewTotal} unrooted components
              </span>
              <button
                type="button"
                onClick={() => setSkip(s => s + REVIEW_QUEUE_FETCH_LIMIT)}
                disabled={skip + REVIEW_QUEUE_FETCH_LIMIT >= reviewTotal}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              The CSV export covers the page shown here — page through to export the rest.
            </p>
          </>
        )}
        {unrootedQuery.isLoading && (
          <div className="text-sm text-gray-500 py-6 text-center">Loading...</div>
        )}
        {unrootedQuery.isError && (
          <div className="text-sm text-red-600 py-6 text-center">
            Couldn&apos;t load the unrooted worklist:{' '}
            {unrootedQuery.error instanceof Error ? unrootedQuery.error.message : 'unknown error'}
          </div>
        )}
        {!unrootedQuery.isLoading && !unrootedQuery.isError && unrooted.length === 0 && (
          <div className="text-sm text-gray-500 py-6 text-center">
            No unrooted secondary runs — every run is connected to a transformer.
          </div>
        )}
        {unrooted.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="p-2">Component</th>
                  <th className="p-2">Feeder</th>
                  <th className="p-2 text-right">Meters</th>
                  <th className="p-2 text-right">Anchor segment</th>
                  <th className="p-2">Candidates</th>
                </tr>
              </thead>
              <tbody>
                {unrooted.map(c => (
                  <tr key={`${c.run_id}-${c.component_id}`} className="border-b border-gray-100">
                    <td className="p-2 text-xs text-gray-700">
                      #{c.component_id} (run {c.run_id})
                    </td>
                    <td className="p-2 text-xs text-gray-600">{c.feeder_name ?? '—'}</td>
                    <td className="p-2 text-xs text-gray-600 text-right">{c.meter_count}</td>
                    <td className="p-2 text-xs text-gray-600 text-right">
                      {c.anchor_segment_id ?? '—'}
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {c.candidates.length === 0
                        ? '—'
                        : c.candidates
                            .map(
                              cand =>
                                `${cand.transformer_name ?? 'transformer'} #${cand.transformer_id}`,
                            )
                            .join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-gray-700">
            Fed-by mismatches <span className="text-gray-400 ml-1">({fedTotal})</span>
          </h3>
          <button
            type="button"
            onClick={() => exportCsv(csvFilename('fed-by-mismatches'), fedByCsvRows(fedRows))}
            disabled={fedRows.length === 0}
            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export fed-by mismatches (CSV)
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          The map data records what feeds each transformer and cabinet. These rows are
          the ones where that record names a device the model does not have on the
          path to the source — usually a mis-drawn cable or a stale record. Confirm in
          the field, or export the list for the GIS editor.
        </p>
        {fedPaged && (
          <>
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setFedSkip(s => Math.max(0, s - FED_BY_FETCH_LIMIT))}
                disabled={fedSkip === 0}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Showing {fedSkip + 1}–{fedSkip + fedRows.length} of {fedTotal} mismatches
              </span>
              <button
                type="button"
                onClick={() => setFedSkip(s => s + FED_BY_FETCH_LIMIT)}
                disabled={fedSkip + FED_BY_FETCH_LIMIT >= fedTotal}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              The CSV export covers the page shown here — page through to export the rest.
            </p>
          </>
        )}
        {fedQuery.isLoading && (
          <div className="text-sm text-gray-500 py-6 text-center">Loading...</div>
        )}
        {fedQuery.isError && (
          <div className="text-sm text-red-600 py-6 text-center">
            Couldn&apos;t load the fed-by worklist:{' '}
            {fedQuery.error instanceof Error ? fedQuery.error.message : 'unknown error'}
          </div>
        )}
        {!fedQuery.isLoading && !fedQuery.isError && fedRows.length === 0 && (
          <div className="text-sm text-gray-500 py-6 text-center">
            {fedRunId == null
              ? 'No completed protection run yet, so there is nothing to compare the map data against.'
              : `No fed-by mismatches on protection run ${fedRunId}.`}
          </div>
        )}
        {fedRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="p-2">Record</th>
                  <th className="p-2">Feeder</th>
                  <th className="p-2">Map data says</th>
                  <th className="p-2">Model says</th>
                  <th className="p-2">Why</th>
                  <th className="p-2 text-right">Gap</th>
                </tr>
              </thead>
              <tbody>
                {fedRows.map(f => (
                  <tr key={f.id} className="border-b border-gray-100">
                    <td className="p-2 text-xs text-gray-700">
                      {f.entity_name ?? `${f.entity_type} #${f.entity_id}`}
                    </td>
                    <td className="p-2 text-xs text-gray-600">{f.feeder_name ?? '—'}</td>
                    <td className="p-2 text-xs text-gray-600">
                      {f.pointer_device_name ?? f.pointer_raw ?? '—'}
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {f.computed_device_name ?? '—'}
                    </td>
                    {/* `pointer_feeder_id` / `reason` are lifted onto the row so
                        the line is actionable without a second lookup; each is
                        only set for its own classification, so read it
                        defensively. */}
                    <td className="p-2 text-xs text-gray-600">
                      {f.classification === 'other_feeder' ? (
                        <>
                          Names a device on another circuit
                          {f.pointer_feeder_id != null && (
                            <span className="text-gray-400"> (feeder #{f.pointer_feeder_id})</span>
                          )}
                        </>
                      ) : f.classification === 'unresolved' ? (
                        <>
                          No usable record
                          {f.reason && (
                            <span className="text-gray-400">
                              {' '}
                              ({UNRESOLVED_REASON[f.reason] ?? f.reason})
                            </span>
                          )}
                        </>
                      ) : (
                        'Not on the path to the source'
                      )}
                    </td>
                    <td className="p-2 text-xs text-gray-600 text-right">
                      {f.distance_m != null ? `${Math.round(f.distance_m)} m` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-gray-700">
            Secondary disagreements <span className="text-gray-400 ml-1">({secTotal})</span>
          </h3>
          <button
            type="button"
            onClick={() =>
              exportCsv(csvFilename('secondary-disagreements'), secondaryDisagreementCsvRows(secRows))
            }
            disabled={secRows.length === 0}
            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export secondary-disagreements worklist (CSV)
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          The low-voltage network was traced from each transformer to each meter. These meters
          are attached to a different transformer than the one they are linked to. Accept the
          secondary&apos;s answer in the Secondary Review page, or fix the link in GIS. Rows
          already accepted stay listed with "Decided = yes" until the next secondary recompute
          republishes the run; the agreement flag is recorded when a run is published, not live.
        </p>
        <label className="flex items-center gap-2 text-xs text-gray-600 mb-3">
          <input
            type="checkbox"
            checked={secFar}
            disabled={secThreshold == null}
            onChange={e => {
              setSecFar(e.target.checked)
              // Toggling the filter changes the total, so a stale skip past the new total would otherwise return an empty page.
              setSecSkip(0)
            }}
          />
          Only &gt; {secThreshold} m from the linked transformer
        </label>
        {secPaged && (
          <>
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setSecSkip(s => Math.max(0, s - EVIDENCE_FETCH_LIMIT))}
                disabled={secSkip === 0}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Showing {secSkip + 1}–{secSkip + secRows.length} of {secTotal} secondary
                disagreements
              </span>
              <button
                type="button"
                onClick={() => setSecSkip(s => s + EVIDENCE_FETCH_LIMIT)}
                disabled={secSkip + EVIDENCE_FETCH_LIMIT >= secTotal}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              The CSV export covers the page shown here — page through to export the rest.
            </p>
          </>
        )}
        {secQuery.isLoading && (
          <div className="text-sm text-gray-500 py-6 text-center">Loading...</div>
        )}
        {secQuery.isError && (
          <div className="text-sm text-red-600 py-6 text-center">
            Couldn&apos;t load the secondary-disagreements worklist:{' '}
            {secQuery.error instanceof Error ? secQuery.error.message : 'unknown error'}
          </div>
        )}
        {!secQuery.isLoading && !secQuery.isError && secRows.length === 0 && (
          <div className="text-sm text-gray-500 py-6 text-center">
            No secondary disagreements on the published runs.
          </div>
        )}
        {secRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="p-2">Meter</th>
                  <th className="p-2">Feeder</th>
                  <th className="p-2">Tier</th>
                  <th className="p-2">Linked transformer</th>
                  <th className="p-2">Secondary says</th>
                  <th className="p-2 text-right">Component meters</th>
                  <th className="p-2">Decided</th>
                  <th className="p-2 text-right">Gap</th>
                </tr>
              </thead>
              <tbody>
                {secRows.map(d => (
                  <tr key={`${d.run_id}-${d.meter_id}`} className="border-b border-gray-100">
                    <td className="p-2 text-xs text-gray-700">
                      {d.meter_number ?? d.gis_id ?? `#${d.meter_id}`}
                    </td>
                    <td className="p-2 text-xs text-gray-600">{d.feeder_name ?? '—'}</td>
                    <td className="p-2 text-xs text-gray-600">{d.tier}</td>
                    <td className="p-2 text-xs text-gray-600">
                      {d.linked_transformer_name ?? d.linked_transformer_id}
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {d.secondary_transformer_name ?? d.secondary_transformer_id}
                    </td>
                    <td className="p-2 text-xs text-gray-600 text-right">
                      {d.component_meter_count}
                    </td>
                    <td className="p-2 text-xs text-gray-600">{d.override_active ? 'yes' : ''}</td>
                    <td className="p-2 text-xs text-gray-600 text-right">
                      {d.distance_to_linked_m != null
                        ? `${Math.round(d.distance_to_linked_m)} m`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-gray-700">
            Pole says a different transformer{' '}
            <span className="text-gray-400 ml-1">({poleTotal})</span>
          </h3>
          <button
            type="button"
            onClick={() =>
              exportCsv(csvFilename('pole-disagreements'), poleDisagreementCsvRows(poleRows))
            }
            disabled={poleRows.length === 0}
            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export pole-disagreements worklist (CSV)
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Each meter&apos;s record names the pole it hangs from. These poles carry a transformer
          other than the one the meter is linked to — or the meter is not linked at all.
        </p>
        <label className="flex items-center gap-2 text-xs text-gray-600 mb-3">
          <input
            type="checkbox"
            checked={poleFar}
            disabled={poleThreshold == null}
            onChange={e => {
              setPoleFar(e.target.checked)
              // Toggling the filter changes the total, so a stale skip past the new total would otherwise return an empty page.
              setPoleSkip(0)
            }}
          />
          Only &gt; {poleThreshold} m from the linked transformer
        </label>
        {polePaged && (
          <>
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setPoleSkip(s => Math.max(0, s - EVIDENCE_FETCH_LIMIT))}
                disabled={poleSkip === 0}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Showing {poleSkip + 1}–{poleSkip + poleRows.length} of {poleTotal} pole
                disagreements
              </span>
              <button
                type="button"
                onClick={() => setPoleSkip(s => s + EVIDENCE_FETCH_LIMIT)}
                disabled={poleSkip + EVIDENCE_FETCH_LIMIT >= poleTotal}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              The CSV export covers the page shown here — page through to export the rest.
            </p>
          </>
        )}
        {poleQuery.isLoading && (
          <div className="text-sm text-gray-500 py-6 text-center">Loading...</div>
        )}
        {poleQuery.isError && (
          <div className="text-sm text-red-600 py-6 text-center">
            Couldn&apos;t load the pole-disagreements worklist:{' '}
            {poleQuery.error instanceof Error ? poleQuery.error.message : 'unknown error'}
          </div>
        )}
        {!poleQuery.isLoading && !poleQuery.isError && poleRows.length === 0 && (
          <div className="text-sm text-gray-500 py-6 text-center">
            Every pole reference agrees with the linked transformer.
          </div>
        )}
        {poleRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="p-2">Meter</th>
                  <th className="p-2">Feeder</th>
                  <th className="p-2">Pole</th>
                  <th className="p-2">Linked transformer</th>
                  <th className="p-2">Pole says</th>
                  <th className="p-2 text-right">Gap</th>
                </tr>
              </thead>
              <tbody>
                {poleRows.map(pd => (
                  <tr key={pd.meter_id} className="border-b border-gray-100">
                    <td className="p-2 text-xs text-gray-700">
                      {pd.meter_number ?? pd.gis_id ?? `#${pd.meter_id}`}
                    </td>
                    <td className="p-2 text-xs text-gray-600">{pd.feeder_name ?? '—'}</td>
                    <td className="p-2 text-xs text-gray-600">{pd.pole_ref}</td>
                    <td className="p-2 text-xs text-gray-600">
                      {pd.linked_transformer_name ?? pd.linked_transformer_id}
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {pd.pole_transformer_names
                        .map((n, k) => n ?? `#${pd.pole_transformer_ids[k]}`)
                        .join('; ')}
                    </td>
                    <td className="p-2 text-xs text-gray-600 text-right">
                      {pd.distance_to_linked_m != null
                        ? `${Math.round(pd.distance_to_linked_m)} m`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-gray-700">
            Poles with no platform transformer{' '}
            <span className="text-gray-400 ml-1">({noXfmrTotal})</span>
          </h3>
          <button
            type="button"
            onClick={() =>
              exportCsv(
                csvFilename('poles-without-transformer'),
                polesWithoutTransformerCsvRows(noXfmrRows),
              )
            }
            disabled={noXfmrRows.length === 0}
            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export poles-without-transformer worklist (CSV)
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          These pole references match no active transformer in the platform. A GIS item, not a
          meter fix: either the pole id is wrong on the meter or the transformer is missing from
          the export.
        </p>
        {noXfmrPaged && (
          <>
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setNoXfmrSkip(s => Math.max(0, s - EVIDENCE_FETCH_LIMIT))}
                disabled={noXfmrSkip === 0}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Showing {noXfmrSkip + 1}–{noXfmrSkip + noXfmrRows.length} of {noXfmrTotal} poles
              </span>
              <button
                type="button"
                onClick={() => setNoXfmrSkip(s => s + EVIDENCE_FETCH_LIMIT)}
                disabled={noXfmrSkip + EVIDENCE_FETCH_LIMIT >= noXfmrTotal}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              The CSV export covers the page shown here — page through to export the rest.
            </p>
          </>
        )}
        {noXfmrQuery.isLoading && (
          <div className="text-sm text-gray-500 py-6 text-center">Loading...</div>
        )}
        {noXfmrQuery.isError && (
          <div className="text-sm text-red-600 py-6 text-center">
            Couldn&apos;t load the poles-without-transformer worklist:{' '}
            {noXfmrQuery.error instanceof Error ? noXfmrQuery.error.message : 'unknown error'}
          </div>
        )}
        {!noXfmrQuery.isLoading && !noXfmrQuery.isError && noXfmrRows.length === 0 && (
          <div className="text-sm text-gray-500 py-6 text-center">
            Every pole reference matches a transformer.
          </div>
        )}
        {noXfmrRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="p-2">Pole</th>
                  <th className="p-2 text-right">Meters</th>
                  <th className="p-2">Feeder</th>
                  <th className="p-2">Sample meter ids</th>
                </tr>
              </thead>
              <tbody>
                {noXfmrRows.map(p => (
                  <tr key={p.pole_ref} className="border-b border-gray-100">
                    <td className="p-2 text-xs text-gray-700">{p.pole_ref}</td>
                    <td className="p-2 text-xs text-gray-600 text-right">{p.meter_count}</td>
                    <td className="p-2 text-xs text-gray-600">{p.feeder_name ?? '—'}</td>
                    <td className="p-2 text-xs text-gray-600">{p.sample_meter_ids.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
